import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { Image } from 'primereact/image'
import {
  AHCREvent,
  genderMapping,
  booleanMapping,
  colorMapping,
  jacketTypeMapping,
  trousersTypeMapping,
  hairStyleMapping,
  ageGroupMapping,
} from '../types/ahcr-event.types'
import { formatStayDuration, isLoitering } from '../utils/format-stay-duration'

const translate = (
  value: string | null,
  mapping: Record<string, string>
): string => {
  if (!value) return 'N/A'
  return mapping[value.toLowerCase()] || value
}

export interface AHCREventDetailsViewerProps {
  AHCREvent: AHCREvent
  visible: boolean
  hideDialog: () => void
  setSelectedImage: (selectedImage: string | null) => void
}

const AHCREventDetailViewer = (props: AHCREventDetailsViewerProps) => {
  const ahcrEventDialogFooter = (
    <div className='events-dialog-footer'>
      <Button
        label='Fechar'
        icon='pi pi-times'
        outlined
        className='events-btn-close'
        onClick={props.hideDialog}
      />
    </div>
  )

  return (
    <Dialog
      visible={props.visible}
      key={props.AHCREvent.uId}
      style={{ width: '700px' }}
      header={
        <div className='events-dialog-header'>
          <i className='pi pi-info-circle events-dialog-icon' />
          <span>Detalhes do Evento</span>
        </div>
      }
      modal
      className='events-dialog'
      footer={ahcrEventDialogFooter}
      onHide={props.hideDialog}
    >
      <div className='events-details'>
        <div className='events-details-grid'>
          <div className='events-detail-section'>
            <h4 className='events-section-title'>
              <i className='pi pi-user' />
              Informações Gerais
            </h4>
            <Divider />

            <div className='events-detail-row'>
              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-video' />
                  Câmera
                </label>
                <span className='events-detail-value'>
                  {props.AHCREvent.cameraName || 'N/A'}
                </span>
              </div>

              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-calendar' />
                  Data de Detecção
                </label>
                <span className='events-detail-value'>
                  {new Date(props.AHCREvent.createdDate).toLocaleString(
                    'pt-BR'
                  )}
                </span>
              </div>
            </div>

            <div className='events-detail-row'>
              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-users' />
                  Gênero
                </label>
                <span className='events-detail-value'>
                  {translate(props.AHCREvent.gender, genderMapping)}
                </span>
              </div>

              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-id-card' />
                  Faixa Etária
                </label>
                <span className='events-detail-value'>
                  {translate(props.AHCREvent.ageGroup, ageGroupMapping)}
                </span>
              </div>
            </div>

            {props.AHCREvent.stayDuration !== null && (
              <div className='events-detail-row'>
                <div className='events-detail-item'>
                  <label
                    className='events-detail-label'
                    style={isLoitering(props.AHCREvent.stayDuration) ? { color: 'var(--red-500)' } : undefined}
                  >
                    <i className='pi pi-stopwatch' />
                    Permanência no Local
                  </label>
                  <span
                    className='events-detail-value'
                    style={isLoitering(props.AHCREvent.stayDuration) ? { color: 'var(--red-500)', fontWeight: 600 } : undefined}
                  >
                    {formatStayDuration(props.AHCREvent.stayDuration)}
                    {isLoitering(props.AHCREvent.stayDuration) && ' — possível vadiagem'}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className='events-detail-section'>
            <h4 className='events-section-title'>
              <i className='pi pi-eye' />
              Acessórios
            </h4>
            <Divider />

            <div className='events-detail-row'>
              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-sun' />
                  Chapéu
                </label>
                <span className='events-detail-value'>
                  {translate(props.AHCREvent.hat, booleanMapping)}
                </span>
              </div>

              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-shield' />
                  Máscara
                </label>
                <span className='events-detail-value'>
                  {translate(props.AHCREvent.mask, booleanMapping)}
                </span>
              </div>
            </div>

            <div className='events-detail-row'>
              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-eye' />
                  Óculos
                </label>
                <span className='events-detail-value'>
                  {translate(props.AHCREvent.glass, booleanMapping)}
                </span>
              </div>

              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-briefcase' />
                  Objetos
                </label>
                <span className='events-detail-value'>
                  {translate(props.AHCREvent.things, booleanMapping)}
                </span>
              </div>
            </div>

            <div className='events-detail-row'>
              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-shopping-bag' />
                  Bolsa
                </label>
                <span className='events-detail-value'>
                  {translate(props.AHCREvent.bag, booleanMapping)}
                </span>
              </div>
            </div>
          </div>

          <div className='events-detail-section'>
            <h4 className='events-section-title'>
              <i className='pi pi-tag' />
              Aparência
            </h4>
            <Divider />

            <div className='events-detail-row'>
              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-user' />
                  Estilo de Cabelo
                </label>
                <span className='events-detail-value'>
                  {translate(props.AHCREvent.hairStyle, hairStyleMapping)}
                </span>
              </div>

              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-tag' />
                  Tipo de Blusa
                </label>
                <span className='events-detail-value'>
                  {translate(props.AHCREvent.jacketType, jacketTypeMapping)}
                </span>
              </div>
            </div>

            <div className='events-detail-row'>
              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-palette' />
                  Cor da Blusa
                </label>
                <span className='events-detail-value'>
                  {translate(props.AHCREvent.jacketColor, colorMapping)}
                </span>
              </div>

              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-tag' />
                  Tipo de Calça
                </label>
                <span className='events-detail-value'>
                  {translate(props.AHCREvent.trousersType, trousersTypeMapping)}
                </span>
              </div>
            </div>

            <div className='events-detail-row'>
              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-palette' />
                  Cor da Calça
                </label>
                <span className='events-detail-value'>
                  {translate(props.AHCREvent.trousersColor, colorMapping)}
                </span>
              </div>
            </div>
          </div>

          <div className='events-detail-section'>
            <h4 className='events-section-title'>
              <i className='pi pi-image' />
              Imagens Capturadas
            </h4>
            <Divider />

            <div className='events-images-grid'>
              <div className='events-image-detail'>
                <label className='events-image-label'>Imagem Aproximada</label>
                {props.AHCREvent.closeUpImageUrl ? (
                  <div
                    className='events-image-preview'
                    onClick={() =>
                      props.setSelectedImage(props.AHCREvent.closeUpImageUrl)
                    }
                  >
                    <Image
                      src={props.AHCREvent.closeUpImageUrl}
                      alt='Aproximada'
                      className='events-preview-image'
                      width='200px'
                    />
                    <div className='events-preview-overlay'>
                      <i className='pi pi-search-plus' />
                      <span>Clique para ampliar</span>
                    </div>
                  </div>
                ) : (
                  <div className='events-no-image-large'>
                    <i className='pi pi-image' />
                    <span>Imagem não disponível</span>
                  </div>
                )}
              </div>

              <div className='events-image-detail'>
                <label className='events-image-label'>Imagem da Detecção</label>
                {props.AHCREvent.detectionImageUrl ? (
                  <div
                    className='events-image-preview'
                    onClick={() =>
                      props.setSelectedImage(props.AHCREvent.detectionImageUrl)
                    }
                  >
                    <Image
                      src={props.AHCREvent.detectionImageUrl}
                      alt='Detecção'
                      className='events-preview-image'
                      width='200px'
                    />
                    <div className='events-preview-overlay'>
                      <i className='pi pi-search-plus' />
                      <span>Clique para ampliar</span>
                    </div>
                  </div>
                ) : (
                  <div className='events-no-image-large'>
                    <i className='pi pi-image' />
                    <span>Imagem não disponível</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default AHCREventDetailViewer
